import React, {useCallback, useEffect, useMemo, useState} from "react";
import Button from "../../../../../components/base/button/button";
import {Col} from "reactstrap";
import {Add, Edit} from "@mui/icons-material";
import Api from "../../../../../../core/services/api/api";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Box, CircularProgress, Paper, SvgIcon, Typography} from "@mui/material";
import TryAgain from "../../../../../components/app-specific/try-again";
import classnames from "classnames";
import UploadBannerImageDialog from "../../../../../components/dialogs/upload-banner-image";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import IconButton from "../../../../../components/base/button/icon_button";
import Banner from "../../../../../components/app-specific/banner";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../../../../components/base/chips/chips";
import TagChipsContent from "../../../../../components/app-specific/chips-contents/tag-chips-content";
import {ReactComponent as RemoveIcon} from "../../../../../../assets/images/remove.svg";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useIndustry from "../../../../../hooks/use-industry";
import UIUtils from "../../../../../utils";
import TagAutocomplete from "../../../../../components/text-fields/autocomplete/tag-autocomplete";


const UserPlatformBannerManagementView = () => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addNewBannerDialog, setAddNewBannerDialog] = useState({open: false});
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('user-platform-banner-managements-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.userPlatformBannerManagement);
        configureBehavioralTool((tags) => tags.userPlatformBannerManagement);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * As soon as the component mounts:
     * - fetches the list of banners from the server.
     */
    useEffect(() => {
        getBanners().then();
    }, [])

    /**
     * Fetches the list of banners from the server.
     * @return {Promise<void>}
     */
    const getBanners = async () => {
        setLoading(true);
        const response = await Api.getBannersList();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setBanners(response?.data ?? []);
        }
        setLoading(false);
    }

    /**
     * Updates the banner's information with the given ban ner in the state list
     * @param banner
     */
    const updateBanner = (banner) => {
        setBanners(prevState => prevState.map(e => e.id === banner.id
            ? {...e, ...banner}
            : e
        ))
    }

    /**
     * Remvoes the banner associated with the given banner's id from the state list.
     * @param banner
     */
    const removeBanner = (banner) => {
        setBanners(prevState => prevState.filter(e => e.id !== banner.id))
    }


    /**
     * Adds the newly created banner from the list of banners
     * @param banner
     */
    const addNewBanner = (banner) => {
        setBanners(prevState => [banner, ...prevState,])
    }

    return (
        <>
            <div className={'banner-management-view'}>
                <div className={'d-flex align-items-center justify-content-between'}>
                    <p className={'view-title'}>
                        Banners Management
                    </p>
                    <Button
                        className='d-flex justify-content-center align-items-center'
                        variant={'outlined'}
                        color={'primary'}
                        onClick={() => setAddNewBannerDialog({open: true})}
                    >
                        <Add className='icon normal p-1 mr-1'/>
                        <p className={'button-label'}>Add new banner</p>
                    </Button>
                </div>
                <Paper className={classnames('paper', {'masonry-columns': !loading && banners.length})}>
                    {
                        loading
                            ? Array(6).fill(null).map((_, e) => (
                                <Col key={e} xs={12} md={6} className={'loading extraLarge mb-2'}>
                                    <div/>
                                </Col>
                            ))
                            : banners.length
                                ? banners?.map(banner => (
                                    <BannerManagementCard
                                        key={banner.id}
                                        banner={banner}
                                        update={updateBanner}
                                        remove={removeBanner}
                                    />
                                ))
                                : <TryAgain
                                    text={"There are currently no banners in your system. You may create a new banner by" +
                                        " clicking on the below button."}
                                    buttonText={'add a new banner'}
                                    showButton={true}
                                    onClick={() => setAddNewBannerDialog({open: true})}
                                />
                    }

                </Paper>
            </div>
            <UploadBannerImageDialog
                open={addNewBannerDialog.open}
                close={() => setAddNewBannerDialog({open: false})}
                onSuccess={addNewBanner}
            />
        </>
    )
}


const BannerManagementCard = ({banner, update, remove}) => {
    const [addingTag, setAddingTag] = useState(false);
    const [removingTags, setRemovingTags] = useState([]);
    const [uploadDialog, setUploadDialog] = useState(false);
    const [removing, setRemoving] = useState(false);
    const isMounted = useIsMounted();

    const {industries} = useIndustry({noCategories: true})

    const tags = useMemo(() => banner?.tags ?? [], [banner]);
    const industryIdFromTags = useMemo(() => tags?.find(e => !!e.industryId)?.industryId ?? null, [tags]);
    const [fetchedIndustryId, setFetchedIndustryId] = useState(industryIdFromTags);

    const industry = useMemo(() => industries.find(industry => industry.id === fetchedIndustryId), [industries, fetchedIndustryId]);

    /**
     * Fetches the industry id from the server by the given tags list.
     * @type {(function(*): Promise<void>)|*}
     */
    const fetchIndustryIdFromTags = useCallback(async (tags) => {
        const response = await Api.getTagsListByIds({ids: tags.map(e => e.id)})
        if (!isMounted()) return;
        if (response?.resultFlag) {
            const fetchedTags = response?.data ?? [];
            const industryId = fetchedTags?.find(e => !!e.industryId)?.industryId ?? null;
            setFetchedIndustryId(industryId);
        }
    }, [isMounted])

    /**
     * As soon as the tags of the banner are loaded / changed:
     * - if there is an industry id inside the tags and it is not yet fetched, fetches the industry id from the server.
     */
    useEffect(() => {
        if (!industryIdFromTags && !!tags.length) {
            void fetchIndustryIdFromTags(tags);
        }
    }, [tags, industryIdFromTags, fetchIndustryIdFromTags])

    /**
     * As soon as the industry id from the tags is changed:
     */
    useEffect(() => {
        setFetchedIndustryId(industryIdFromTags);
    }, [banner?.id, industryIdFromTags])

    /**
     * Removes the current banner from the server.
     *
     * * if the response of the api is successful, invokes the remove callback.
     * @return {Promise<void>}
     */
    const removeBanner = async () => {
        setRemoving(true);
        const response = await Api.removeBanner(banner.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            remove(banner);
        } else {
            setRemoving(false);
        }
    }

    /**
     * Updates the image of the current banner by invoking the update callback.
     * @param {any} updatedBanner
     */
    const updateBannerImage = (updatedBanner) => {
        update({
            ...banner,
            ...(updatedBanner ?? {}),
            imageFileInfo: {
                ...banner.imageFileInfo,
                ...(updatedBanner?.imageFileInfo ?? {})
            },
        })
    }

    /**
     * Adds a new tag to the current banner in the server.
     *
     * * if the response of the api is successful, invokes the update callback as well.
     * @param {any} tag
     * @return {Promise<void>}
     */
    const addNewTag = async (tag) => {
        setAddingTag(true);
        const response = await Api.addTagToBanner(banner.id, tag.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            update({
                ...banner,
                tags: [...(banner.tags ?? []), tag],
            });
        }
        setAddingTag(false);
    }

    /**
     * Removes the given tag form the current banner in the server.
     *
     * * if the result of the api is successful, invokes the update callback as well.
     * @param tag
     * @return {Promise<void>}
     */
    const removeTag = async (tag) => {
        setRemovingTags(prevState => [...prevState, tag.id]);
        const response = await Api.removeTagFromBanner(banner.id, tag.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            update({
                ...banner,
                tags: banner?.tags?.filter(e => e.id !== tag.id) ?? [],
            });
        }
        setRemovingTags(prevState => prevState.filter(e => e !== tag.id));
    }

    /**
     * Gets users confirmation before removing the current banner entirely.
     */
    const getRemoveConfirmation = () => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Do you want to remove the selected banner?
                </p>
            </div>,
            {
                maxWidth: 'sm'
            },
            (confirmed) => confirmed && removeBanner().then(),
        );
    }

    return (
        <>
            <div className={'masonry-card banner-management-card'}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                        {
                            !!industry &&
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: 1,
                                padding: 1,
                                color: 'primary.main',
                                backgroundColor: 'background.default',
                                marginRight: 1,
                            }}>
                                {
                                    React.cloneElement(UIUtils.getIndustryIcon(industry?.iconFileName, industry?.id, true) ??
                                        <div/>, {style: {width: 24, height: 24, color: 'inherit'}})
                                }
                            </Box>
                        }
                        {
                            !!industry &&
                            <Typography variant={'caption'}>
                                {industry?.title ?? ""}
                            </Typography>
                        }
                    </Box>
                    <div className={'header-section w-auto'}>
                        <IconButton
                            disabled={removing}
                            className={"mx-1"}
                            color={'paper.secondary'}
                            onPressed={() => setUploadDialog(true)}
                        >
                            <Edit className={"icon normal"}/>
                        </IconButton>
                        <IconButton
                            onPressed={getRemoveConfirmation}
                            disabled={removing}
                            color={'paper.error'}
                        >
                            {
                                removing
                                    ? <CircularProgress size={15}/>
                                    : <SvgIcon component={RemoveIcon} className='icon normal'/>
                            }
                        </IconButton>
                    </div>
                </Box>
                <Banner
                    withShadow
                    url={banner?.imageFileInfo?.url}
                    initialHeight={300}
                    className={'my-2'}
                />
                <div className={'tags-container'}>
                    {
                        banner?.tags?.map(tag => {
                            const removingTag = !!removingTags.find(e => e === tag.id);
                            return (
                                <Chips
                                    key={tag.id}
                                    type={removingTag ? ChipsTypes.loading : ChipsTypes.remove}
                                    content={<TagChipsContent tag={tag} useIcon/>}
                                    onIconClick={() => removeTag(tag)}
                                    className={'mr-2 mb-2'}
                                    contentType={ChipsContentType.element}
                                    iconPosition={ChipsIconPosition.start}
                                    disabled={removing || removingTag}
                                    buttonProps={{
                                        size: 'small',
                                    }}
                                />
                            )
                        })
                    }
                </div>
                <TagAutocomplete
                    value={null}
                    clearOnBlur
                    selectedTags={banner?.tags ?? []}
                    disabled={removing || addingTag}
                    onChange={(e, t) => addNewTag(t)}
                    industryId={industry?.id}
                    label={'New Tag'}
                />
            </div>
            <UploadBannerImageDialog
                open={uploadDialog}
                close={() => setUploadDialog(false)}
                bannerId={banner?.id}
                bannerUrl={banner?.imageFileInfo?.url}
                onSuccess={updateBannerImage}
            />
        </>
    );
}


export default UserPlatformBannerManagementView;
